import type { SpendingItem } from '../../domain/spending';
import { DEFAULT_CATEGORIES } from '../../domain/defaultCategories';
import { dueDateIn, periodOf } from '../../domain/recurring';
import {
  BUDGET_TYPES,
  DEBT_TYPES,
  TRANSACTION_TYPES,
  ValidationError,
  requireCurrency,
  requireIsoDate,
  requirePositiveMinor,
  requireText,
  spendingRule,
  type ActivityAction,
  type ActivityEntry,
  type BudgetMember,
  type BudgetSpace,
  type BudgetType,
  type Category,
  type ConsumptionData,
  type DebtDetails,
  type Frequency,
  type IsoDate,
  type Ownership,
  type RecurringKind,
  type RecurringTemplate,
  type Transaction,
  type TransactionType,
  type User,
} from '../../domain/model';
import type { Statement } from '../db/driver';
import { EntityTable, NotFoundError, Repo, type NewEntity, type Patch, type StoreContext } from './entity';
import {
  activitySpec,
  categorySpec,
  consumptionSpec,
  debtSpec,
  memberSpec,
  recurringSpec,
  spaceSpec,
  subcategorySpec,
  transactionSpec,
  userSpec,
} from './specs';

/** Local calendar date for "today" (not UTC: an expense at 23:30 belongs to today). */
export function todayIso(now: number = Date.now()): IsoDate {
  const d = new Date(now);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export interface NewSpaceInput {
  name: string;
  type: BudgetType;
  currency: string;
  billingCountry?: string | null;
  monthlyTargetMinor?: number | null;
  ownerUserId: string;
  ownerDisplayName: string;
  /** Seed the default category tree (default true). */
  withDefaultCategories?: boolean;
}

/**
 * Everything the minimum entry needs is category + amount + who added it;
 * the rest has a default: type expense, today, the budget's currency,
 * paid by whoever added it, personal in a Personal budget and shared in a
 * Household.
 */
export interface NewTransactionInput {
  spaceId: string;
  categoryId: string;
  amountMinor: number;
  addedBy: string;
  subcategoryId?: string | null;
  type?: TransactionType;
  /** Must be the budget's currency when given (amounts are always in it). */
  currency?: string;
  /** Paid in another currency: the amount actually paid; amountMinor is its value in the budget's currency. */
  original?: { amountMinor: number; currency: string } | null;
  date?: IsoDate;
  paidBy?: string;
  ownership?: Ownership;
  ownerMemberId?: string | null;
  /** Required for credit_card_payment (was the card's spending already tracked?). */
  countsAsSpending?: boolean;
  notes?: string | null;
  provider?: string | null;
  dueDate?: IsoDate | null;
  periodStart?: IsoDate | null;
  periodEnd?: IsoDate | null;
  recurringTemplateId?: string | null;
  consumption?: { quantity: number; unit: string; periodStart?: IsoDate | null; periodEnd?: IsoDate | null };
  /** Loan / card state at this payment; only for debt and card payments. Empty fields are fine. */
  debt?: DebtInput;
}

/** Structured filters for finding entries (the entries screen). */
export interface EntryFind {
  spaceIds: string[];
  from?: IsoDate;
  to?: IsoDate;
  categoryId?: string;
  subcategoryId?: string;
  paidBy?: string;
  /** 'shared' or a member id (their own entries). */
  owner?: string;
  minMinor?: number;
  maxMinor?: number;
}

export type DebtInput = Partial<Pick<DebtDetails, 'remainingBalanceMinor' | 'interestRateBp' | 'installmentsLeft'>>;

/** The latest known value of each debt field, with the date of the payment it came from. */
export interface DebtSummary {
  remainingBalanceMinor: { value: number; date: IsoDate } | null;
  interestRateBp: { value: number; date: IsoDate } | null;
  installmentsLeft: { value: number; date: IsoDate } | null;
}

/** A bill that comes back: category + name + fixed/variable; the rest defaults like an entry. */
export interface NewRecurringInput {
  spaceId: string;
  categoryId: string;
  subcategoryId?: string | null;
  name: string;
  kind: RecurringKind;
  /** Required for fixed; for variable it is only an estimate. */
  amountMinor?: number | null;
  type?: TransactionType;
  frequency?: Frequency;
  dayOfMonth?: number | null;
  paidBy?: string | null;
  ownership?: Ownership;
  ownerMemberId?: string | null;
  /** Required for a credit card bill, as on an entry. */
  countsAsSpending?: boolean;
}

export type Store = ReturnType<typeof createStore>;

export function createStore(ctx: StoreContext) {
  const t = {
    users: new EntityTable(ctx, userSpec),
    spaces: new EntityTable(ctx, spaceSpec),
    members: new EntityTable(ctx, memberSpec),
    categories: new EntityTable(ctx, categorySpec),
    subcategories: new EntityTable(ctx, subcategorySpec),
    transactions: new EntityTable(ctx, transactionSpec),
    recurring: new EntityTable(ctx, recurringSpec),
    consumption: new EntityTable(ctx, consumptionSpec),
    debt: new EntityTable(ctx, debtSpec),
    activity: new EntityTable(ctx, activitySpec),
  };

  /* ---------------------------------------------------------- shared checks */

  /**
   * `allowLeft`: someone who has left the budget still counts. Entries and
   * bills keep pointing at the people they were about after those people
   * leave; only new choices must be someone still in the budget.
   */
  async function memberOf(spaceId: string, memberId: string, field: string, allowLeft = false) {
    const m = await t.members.get(memberId, { includeDeleted: allowLeft });
    if (!m || m.spaceId !== spaceId) throw new ValidationError(field, 'is not a member of this budget');
    return m;
  }

  async function categoryOf(spaceId: string, categoryId: string) {
    const c = await t.categories.get(categoryId);
    if (!c || c.spaceId !== spaceId) throw new ValidationError('categoryId', 'is not a category of this budget');
    return c;
  }

  async function subcategoryOf(categoryId: string, subcategoryId: string | null) {
    if (subcategoryId === null) return;
    const s = await t.subcategories.get(subcategoryId);
    if (!s || s.categoryId !== categoryId) throw new ValidationError('subcategoryId', 'does not belong to the category');
  }

  function checkOwnership(o: { ownership: Ownership; ownerMemberId: string | null }) {
    if (o.ownership === 'personal' && !o.ownerMemberId) throw new ValidationError('ownerMemberId', 'required for a personal entry');
    if (o.ownership === 'shared' && o.ownerMemberId) throw new ValidationError('ownerMemberId', 'must be empty for a shared entry');
  }

  function checkSpendingRule(type: TransactionType, countsAsSpending: boolean) {
    const rule = spendingRule(type);
    if (rule !== 'choose' && rule !== countsAsSpending) {
      throw new ValidationError('countsAsSpending', `is fixed to ${rule} for ${type}`);
    }
  }

  function activityStatement(e: Omit<NewEntity<ActivityEntry>, 'at'>): Statement[] {
    return t.activity.prepareCreate({ ...e, at: ctx.now() }).statements;
  }

  /* ---------------------------------------------------------- users */

  const users = Object.assign(
    new Repo(t.users, (u) => {
      requireText('displayName', u.displayName, 80);
    }),
    {
      /** Make sure the device's local identity has a users row (idempotent). */
      async ensureLocal(id: string, displayName: string): Promise<User> {
        return (await t.users.get(id)) ?? users.create({ id, displayName, email: null, kind: 'local' });
      },
    },
  );

  /* ---------------------------------------------------------- spaces */

  function validateSpace(s: BudgetSpace) {
    requireText('name', s.name, 60);
    if (!BUDGET_TYPES.includes(s.type)) throw new ValidationError('type', `unknown budget type ${s.type}`);
    requireCurrency('currency', s.currency);
    if (s.monthlyTargetMinor !== null) requirePositiveMinor('monthlyTargetMinor', s.monthlyTargetMinor);
    if (s.billingCountry !== null && !/^[A-Z]{2}$/.test(s.billingCountry)) throw new ValidationError('billingCountry', 'must be ISO 3166 alpha-2');
  }

  const spaceRepo = new Repo(t.spaces, validateSpace);
  const spaces = Object.assign(spaceRepo, {
    /**
     * A new budget, its owner as first member and (by default) the default
     * category tree, written in one transaction: a budget never exists
     * half-created.
     */
    async createWithOwner(input: NewSpaceInput): Promise<{ space: BudgetSpace; owner: BudgetMember }> {
      if (!(await t.users.get(input.ownerUserId))) throw new ValidationError('ownerUserId', 'unknown user');
      const s = t.spaces.prepareCreate({
        name: input.name.trim(),
        type: input.type,
        currency: input.currency,
        billingCountry: input.billingCountry ?? null,
        monthlyTargetMinor: input.monthlyTargetMinor ?? null,
        ownerUserId: input.ownerUserId,
      });
      validateSpace(s.entity);
      const m = t.members.prepareCreate({
        spaceId: s.entity.id,
        userId: input.ownerUserId,
        displayName: input.ownerDisplayName,
        role: 'owner',
      });
      const statements = [...s.statements, ...m.statements];
      if (input.withDefaultCategories !== false) {
        DEFAULT_CATEGORIES.forEach((dc, i) => {
          const c = t.categories.prepareCreate({
            spaceId: s.entity.id,
            systemKey: dc.key,
            name: null,
            kind: dc.kind,
            nature: dc.nature,
            colorSlot: dc.colorSlot,
            sortOrder: i,
            archivedAt: null,
          });
          statements.push(...c.statements);
          dc.subcategories.forEach((ds, j) => {
            statements.push(
              ...t.subcategories.prepareCreate({
                spaceId: s.entity.id,
                categoryId: c.entity.id,
                systemKey: ds.key,
                name: null,
                defaultUnit: ds.unit ?? null,
                sortOrder: j,
                archivedAt: null,
              }).statements,
            );
          });
        });
      }
      await ctx.db.batch(statements);
      return { space: s.entity, owner: m.entity };
    },

    /** Budgets the user belongs to. */
    async listForUser(userId: string): Promise<BudgetSpace[]> {
      const rows = await t.spaces.select(
        `SELECT s.* FROM budget_spaces s
           JOIN budget_members m ON m.space_id = s.id AND m.deleted_at IS NULL
          WHERE m.user_id = ? AND s.deleted_at IS NULL
          ORDER BY s.created_at`,
        [userId],
      );
      return rows;
    },
  });

  /* ---------------------------------------------------------- members */

  const members = Object.assign(
    new Repo(t.members, async (m) => {
      requireText('displayName', m.displayName, 60);
      if (!(await t.spaces.get(m.spaceId))) throw new NotFoundError('budget_spaces', m.spaceId);
    }),
    {
      /** The member record of a user in a budget (who "I" am there). */
      async forUser(spaceId: string, userId: string): Promise<BudgetMember | undefined> {
        return (await t.members.list({ spaceId, userId }))[0];
      },
    },
  );

  /* ---------------------------------------------------------- categories */

  /** Names are compared trimmed and case-insensitively; archived and deleted rows do not block a name. */
  const norm = (v: string | null) => (v ?? '').trim().toLocaleLowerCase();

  const categories = Object.assign(
    new Repo(t.categories, async (c) => {
      if (c.systemKey === null || c.name !== null) requireText('name', c.name, 40);
      if (!Number.isInteger(c.colorSlot) || c.colorSlot < 0 || c.colorSlot > 7) throw new ValidationError('colorSlot', 'must be 0-7');
      if (!(await t.spaces.get(c.spaceId))) throw new NotFoundError('budget_spaces', c.spaceId);
      if (c.name !== null && !c.archivedAt) {
        const clash = (await t.categories.list({ spaceId: c.spaceId })).find(
          (o) => o.id !== c.id && !o.archivedAt && o.name !== null && norm(o.name) === norm(c.name),
        );
        if (clash) throw new ValidationError('name', 'already used by another category');
      }
    }),
    {
      /** Active categories in display order; archived ones only when asked. */
      async listForSpace(spaceId: string, { includeArchived = false }: { includeArchived?: boolean } = {}) {
        const all = await t.categories.list({ spaceId }, { orderBy: 'sort_order, created_at' });
        return includeArchived ? all : all.filter((c) => !c.archivedAt);
      },
      bySystemKey: async (spaceId: string, key: string) => (await t.categories.list({ spaceId, systemKey: key }))[0],
      /** A user-made category, placed after the existing ones. */
      async createCustom(input: { spaceId: string; name: string; colorSlot: number; kind?: Category['kind']; nature?: Category['nature'] }) {
        const all = await t.categories.list({ spaceId: input.spaceId }, { includeDeleted: true });
        return categories.create({
          spaceId: input.spaceId,
          systemKey: null,
          name: input.name.trim(),
          kind: input.kind ?? 'spending',
          nature: input.nature ?? null,
          colorSlot: input.colorSlot,
          sortOrder: all.reduce((m, c) => Math.max(m, c.sortOrder), -1) + 1,
          archivedAt: null,
        });
      },
      archive: (id: string) => categories.update(id, { archivedAt: ctx.now() }),
      unarchive: (id: string) => categories.update(id, { archivedAt: null }),
    },
  );

  const subcategories = Object.assign(
    new Repo(t.subcategories, async (s) => {
      if (s.systemKey === null || s.name !== null) requireText('name', s.name, 40);
      if (s.defaultUnit !== null && s.defaultUnit.trim().length > 12) throw new ValidationError('defaultUnit', 'longer than 12 characters');
      const c = await categoryOf(s.spaceId, s.categoryId);
      if (c.deletedAt !== null) throw new ValidationError('categoryId', 'category is deleted');
      if (s.name !== null && !s.archivedAt) {
        const clash = (await t.subcategories.list({ categoryId: s.categoryId })).find(
          (o) => o.id !== s.id && !o.archivedAt && o.name !== null && norm(o.name) === norm(s.name),
        );
        if (clash) throw new ValidationError('name', 'already used in this category');
      }
    }),
    {
      async listForCategory(categoryId: string, { includeArchived = false }: { includeArchived?: boolean } = {}) {
        const all = await t.subcategories.list({ categoryId }, { orderBy: 'sort_order, created_at' });
        return includeArchived ? all : all.filter((x) => !x.archivedAt);
      },
      bySystemKey: async (spaceId: string, key: string) => (await t.subcategories.list({ spaceId, systemKey: key }))[0],
      async createCustom(input: { categoryId: string; name: string; defaultUnit?: string | null }) {
        const c = await t.categories.require(input.categoryId);
        const siblings = await t.subcategories.list({ categoryId: c.id }, { includeDeleted: true });
        return subcategories.create({
          spaceId: c.spaceId,
          categoryId: c.id,
          systemKey: null,
          name: input.name.trim(),
          defaultUnit: input.defaultUnit?.trim() || null,
          sortOrder: siblings.reduce((m, x) => Math.max(m, x.sortOrder), -1) + 1,
          archivedAt: null,
        });
      },
      archive: (id: string) => subcategories.update(id, { archivedAt: ctx.now() }),
      unarchive: (id: string) => subcategories.update(id, { archivedAt: null }),
    },
  );

  /** New entries (or an entry moved to another category) need active categories. */
  async function requireActivePick(categoryId: string, subcategoryId: string | null) {
    const c = await t.categories.get(categoryId);
    if (c?.archivedAt) throw new ValidationError('categoryId', 'category is archived');
    if (subcategoryId) {
      const sub = await t.subcategories.get(subcategoryId);
      if (sub?.archivedAt) throw new ValidationError('subcategoryId', 'subcategory is archived');
    }
  }

  /* ---------------------------------------------------------- transactions */

  /** `before`: the entry as it was (an edit): people it already pointed at may have left since. */
  async function validateTransaction(x: Transaction, before?: Transaction) {
    if (!TRANSACTION_TYPES.includes(x.type)) throw new ValidationError('type', `unknown type ${x.type}`);
    requirePositiveMinor('amountMinor', x.amountMinor);
    requireCurrency('currency', x.currency);
    const space = await t.spaces.get(x.spaceId);
    // Totals add amounts as they are: an entry is always in its budget's currency; a foreign payment says so in original*.
    if (space && x.currency !== space.currency) {
      throw new ValidationError('currency', `must be the budget's currency ${space.currency}; put what was paid in ${x.currency} in original`);
    }
    if ((x.originalAmountMinor === null) !== (x.originalCurrency === null)) throw new ValidationError('original', 'amount and currency go together');
    if (x.originalCurrency !== null) {
      requirePositiveMinor('originalAmountMinor', x.originalAmountMinor!);
      requireCurrency('originalCurrency', x.originalCurrency);
      if (x.originalCurrency === x.currency) throw new ValidationError('originalCurrency', 'same as the budget currency');
    }
    requireIsoDate('date', x.date);
    requireIsoDate('dueDate', x.dueDate, true);
    requireIsoDate('periodStart', x.periodStart, true);
    requireIsoDate('periodEnd', x.periodEnd, true);
    if (x.periodStart && x.periodEnd && x.periodStart > x.periodEnd) throw new ValidationError('periodEnd', 'before periodStart');
    if (x.notes && x.notes.length > 1000) throw new ValidationError('notes', 'longer than 1000 characters');
    checkOwnership(x);
    checkSpendingRule(x.type, x.countsAsSpending);
    await categoryOf(x.spaceId, x.categoryId);
    await subcategoryOf(x.categoryId, x.subcategoryId);
    await memberOf(x.spaceId, x.addedBy, 'addedBy', before?.addedBy === x.addedBy);
    await memberOf(x.spaceId, x.paidBy, 'paidBy', before?.paidBy === x.paidBy);
    if (x.ownerMemberId) await memberOf(x.spaceId, x.ownerMemberId, 'ownerMemberId', before?.ownerMemberId === x.ownerMemberId);
  }

  function summaryOf(x: Transaction) {
    return {
      categoryId: x.categoryId,
      subcategoryId: x.subcategoryId,
      type: x.type,
      amountMinor: x.amountMinor,
      currency: x.currency,
      ...(x.originalCurrency && { originalAmountMinor: x.originalAmountMinor, originalCurrency: x.originalCurrency }),
      date: x.date,
    };
  }

  function validateConsumption(c: Pick<ConsumptionData, 'quantity' | 'unit' | 'periodStart' | 'periodEnd'>) {
    if (!Number.isFinite(c.quantity) || c.quantity < 0) throw new ValidationError('consumption.quantity', 'must be zero or more');
    requireText('consumption.unit', c.unit, 12);
    requireIsoDate('consumption.periodStart', c.periodStart, true);
    requireIsoDate('consumption.periodEnd', c.periodEnd, true);
  }

  function debtFields(d: DebtInput) {
    return {
      remainingBalanceMinor: d.remainingBalanceMinor ?? null,
      interestRateBp: d.interestRateBp ?? null,
      installmentsLeft: d.installmentsLeft ?? null,
    };
  }

  function validateDebt(d: ReturnType<typeof debtFields>) {
    if (d.remainingBalanceMinor !== null && (!Number.isInteger(d.remainingBalanceMinor) || d.remainingBalanceMinor < 0)) {
      throw new ValidationError('debt.remainingBalanceMinor', 'must be zero or more');
    }
    if (d.interestRateBp !== null && (!Number.isInteger(d.interestRateBp) || d.interestRateBp < 0 || d.interestRateBp > 100000)) {
      throw new ValidationError('debt.interestRateBp', 'must be 0-1000 %');
    }
    if (d.installmentsLeft !== null && (!Number.isInteger(d.installmentsLeft) || d.installmentsLeft < 0 || d.installmentsLeft > 1200)) {
      throw new ValidationError('debt.installmentsLeft', 'must be a whole number 0-1200');
    }
  }

  const hasDebt = (d: ReturnType<typeof debtFields>) => d.remainingBalanceMinor !== null || d.interestRateBp !== null || d.installmentsLeft !== null;

  /** Rows that go and come back together with an entry (Undo). */
  const attached = [t.consumption, t.debt] as const;

  const transactions = {
    table: t.transactions,

    async create(input: NewTransactionInput): Promise<Transaction> {
      const space = await t.spaces.get(input.spaceId);
      if (!space) throw new NotFoundError('budget_spaces', input.spaceId);
      const type = input.type ?? 'expense';
      const rule = spendingRule(type);
      if (rule === 'choose' && input.countsAsSpending === undefined) {
        throw new ValidationError('countsAsSpending', 'say whether this credit card bill counts as spending');
      }
      if (rule !== 'choose' && input.countsAsSpending !== undefined && input.countsAsSpending !== rule) {
        throw new ValidationError('countsAsSpending', `is fixed to ${rule} for ${type}`);
      }
      const ownership = input.ownership ?? (space.type === 'personal' ? 'personal' : 'shared');
      const { entity, statements } = t.transactions.prepareCreate({
        spaceId: input.spaceId,
        categoryId: input.categoryId,
        subcategoryId: input.subcategoryId ?? null,
        type,
        amountMinor: input.amountMinor,
        currency: input.currency ?? space.currency,
        originalAmountMinor: input.original?.amountMinor ?? null,
        originalCurrency: input.original?.currency ?? null,
        date: input.date ?? todayIso(ctx.now()),
        addedBy: input.addedBy,
        paidBy: input.paidBy ?? input.addedBy,
        ownership,
        ownerMemberId: ownership === 'personal' ? (input.ownerMemberId ?? input.paidBy ?? input.addedBy) : null,
        countsAsSpending: rule === 'choose' ? input.countsAsSpending! : rule,
        notes: input.notes ?? null,
        provider: input.provider ?? null,
        dueDate: input.dueDate ?? null,
        periodStart: input.periodStart ?? null,
        periodEnd: input.periodEnd ?? null,
        recurringTemplateId: input.recurringTemplateId ?? null,
      });
      await validateTransaction(entity);
      await requireActivePick(entity.categoryId, entity.subcategoryId);
      if (input.consumption) {
        const c = { periodStart: null, periodEnd: null, ...input.consumption };
        validateConsumption(c);
        statements.push(...t.consumption.prepareCreate({ spaceId: entity.spaceId, transactionId: entity.id, ...c }).statements);
      }
      if (input.debt) {
        const d = debtFields(input.debt);
        if (hasDebt(d)) {
          if (!DEBT_TYPES.includes(entity.type)) throw new ValidationError('debt', 'only loan and card payments carry debt details');
          validateDebt(d);
          statements.push(...t.debt.prepareCreate({ spaceId: entity.spaceId, transactionId: entity.id, ...d }).statements);
        }
      }
      statements.push(
        ...activityStatement({
          spaceId: entity.spaceId,
          actorMemberId: entity.addedBy,
          action: 'created',
          entity: 'transaction',
          entityId: entity.id,
          summary: summaryOf(entity),
        }),
      );
      await ctx.db.batch(statements);
      return entity;
    },

    get: (id: string, opts?: { includeDeleted?: boolean }) => t.transactions.get(id, opts),

    async update(id: string, patch: Patch<Transaction>, actorMemberId: string | null = null): Promise<Transaction> {
      const current = await t.transactions.require(id);
      const next = { ...patch };
      if (next.type && next.type !== current.type && next.countsAsSpending === undefined) {
        const rule = spendingRule(next.type);
        if (rule === 'choose') throw new ValidationError('countsAsSpending', 'say whether this credit card bill counts as spending');
        next.countsAsSpending = rule;
      }
      if (next.ownership === 'shared') next.ownerMemberId = null;
      // Two devices can each change one half of a pair (type / spending flag, whose / owner) and the
      // merge keeps both: a row that arrived that way is put right on its next edit instead of refusing it.
      const merged = { ...current, ...next };
      const rule = spendingRule(merged.type);
      if (rule !== 'choose' && merged.countsAsSpending !== rule) next.countsAsSpending = rule;
      if (merged.ownership === 'shared' && merged.ownerMemberId) next.ownerMemberId = null;
      if (merged.ownership === 'personal' && !merged.ownerMemberId) next.ownerMemberId = merged.paidBy;
      const { entity, statements } = t.transactions.prepareUpdate(current, next);
      if (statements.length === 0) return current;
      await validateTransaction(entity, current);
      // No longer a loan or card payment: its loan details go with the old type (they would still count as the loan's latest state).
      if (DEBT_TYPES.includes(current.type) && !DEBT_TYPES.includes(entity.type)) {
        for (const d of await t.debt.list({ transactionId: id })) statements.push(...t.debt.prepareTombstone(d, true).statements);
      }
      if (entity.categoryId !== current.categoryId || entity.subcategoryId !== current.subcategoryId) {
        await requireActivePick(entity.categoryId, entity.subcategoryId);
      }
      statements.push(
        ...activityStatement({ spaceId: entity.spaceId, actorMemberId, action: 'updated', entity: 'transaction', entityId: id, summary: summaryOf(entity) }),
      );
      await ctx.db.batch(statements);
      return entity;
    },

    /** Soft delete (Undo = restore). Its consumption and debt details go with it. */
    async remove(id: string, actorMemberId: string | null = null): Promise<Transaction> {
      const current = await t.transactions.require(id);
      const { entity, statements } = t.transactions.prepareTombstone(current, true);
      for (const table of attached) {
        for (const row of await table.list({ transactionId: id })) {
          statements.push(...(table as typeof t.consumption).prepareTombstone(row as ConsumptionData, true, entity.deletedAt!).statements);
        }
      }
      statements.push(
        ...activityStatement({ spaceId: current.spaceId, actorMemberId, action: 'deleted', entity: 'transaction', entityId: id, summary: summaryOf(current) }),
      );
      await ctx.db.batch(statements);
      return entity;
    },

    async restore(id: string, actorMemberId: string | null = null): Promise<Transaction> {
      const current = await t.transactions.get(id, { includeDeleted: true });
      if (!current) throw new NotFoundError('transactions', id);
      if (current.deletedAt === null) return current;
      const { entity, statements } = t.transactions.prepareTombstone(current, false);
      // Bring back the rows removed together with it (same deletion moment).
      for (const table of attached) {
        for (const row of await table.list({ transactionId: id }, { includeDeleted: true })) {
          if (row.deletedAt === current.deletedAt)
            statements.push(...(table as typeof t.consumption).prepareTombstone(row as ConsumptionData, false).statements);
        }
      }
      statements.push(
        ...activityStatement({ spaceId: current.spaceId, actorMemberId, action: 'restored', entity: 'transaction', entityId: id, summary: summaryOf(current) }),
      );
      await ctx.db.batch(statements);
      return entity;
    },

    /** Entries of a budget in [from, to] (inclusive calendar dates), newest first. */
    async listRange(spaceId: string, from: IsoDate, to: IsoDate, filter: { categoryId?: string } = {}): Promise<Transaction[]> {
      const params: (string | number)[] = [spaceId, from, to];
      let extra = '';
      if (filter.categoryId) {
        extra = ' AND category_id = ?';
        params.push(filter.categoryId);
      }
      const rows = await t.transactions.select(
        `SELECT * FROM transactions WHERE space_id = ? AND date BETWEEN ? AND ? AND deleted_at IS NULL${extra} ORDER BY date DESC, id DESC`,
        params,
      );
      return rows;
    },

    /** The most recent live entry for a category (and subcategory, when given): powers "Same as last time". */
    async lastFor(spaceId: string, categoryId: string, subcategoryId: string | null): Promise<Transaction | undefined> {
      const rows = await t.transactions.select(
        `SELECT * FROM transactions
          WHERE space_id = ? AND category_id = ? AND ${subcategoryId ? 'subcategory_id = ?' : 'subcategory_id IS NULL'} AND deleted_at IS NULL
          ORDER BY date DESC, id DESC LIMIT 1`,
        subcategoryId ? [spaceId, categoryId, subcategoryId] : [spaceId, categoryId],
      );
      return rows[0];
    },

    /**
     * The latest live entry of every category/subcategory pair that has one
     * (Quick Add keeps it warm, so "Same as last time" needs no wait). One
     * index lookup per pair (transactions_pick_date), never a pass over the
     * whole budget.
     */
    async latestPerPick(spaceId: string): Promise<Map<string, Transaction>> {
      const rows = await t.transactions.select(
        `SELECT t.* FROM (
           SELECT s.category_id, s.id AS subcategory_id FROM subcategories s WHERE s.space_id = ? AND s.deleted_at IS NULL
           UNION ALL
           SELECT c.id, NULL FROM categories c WHERE c.space_id = ? AND c.deleted_at IS NULL
         ) p
         JOIN transactions t ON t.id = (
           SELECT x.id FROM transactions x
            WHERE x.space_id = ? AND x.category_id = p.category_id AND x.subcategory_id IS p.subcategory_id AND x.deleted_at IS NULL
            ORDER BY x.date DESC, x.id DESC LIMIT 1
         )`,
        [spaceId, spaceId, spaceId],
      );
      return new Map(rows.map((e) => [`${e.categoryId}/${e.subcategoryId ?? ''}`, e]));
    },

    /** Providers already used for a category/subcategory, most recent first: suggestions, never auto-filled. */
    async providersFor(spaceId: string, categoryId: string, subcategoryId: string | null): Promise<string[]> {
      const rows = await ctx.db.all<{ provider: string }>(
        `SELECT provider, MAX(date) AS last FROM transactions
          WHERE space_id = ? AND category_id = ? AND ${subcategoryId ? 'subcategory_id = ?' : 'subcategory_id IS NULL'}
            AND provider IS NOT NULL AND deleted_at IS NULL
          GROUP BY provider ORDER BY last DESC LIMIT 8`,
        subcategoryId ? [spaceId, categoryId, subcategoryId] : [spaceId, categoryId],
      );
      return rows.map((r) => r.provider);
    },

    /**
     * How the last credit card bill was counted (brief §14: "the app can
     * remember the choice for that card"). A card is told apart by its
     * provider (bank); without one, the latest card bill of the budget.
     * Read from the entries themselves, so every device and member sees it.
     */
    async lastCardChoice(spaceId: string, provider?: string | null): Promise<boolean | undefined> {
      const byCard = provider?.trim();
      const rows = await ctx.db.all<{ counts_as_spending: number }>(
        `SELECT counts_as_spending FROM transactions
          WHERE space_id = ? AND type = 'credit_card_payment' AND deleted_at IS NULL${byCard ? ' AND lower(provider) = lower(?)' : ''}
          ORDER BY date DESC, created_at DESC, id DESC LIMIT 1`,
        byCard ? [spaceId, byCard] : [spaceId],
      );
      return rows[0] ? Boolean(rows[0].counts_as_spending) : undefined;
    },

    /** How often each (category, subcategory) was used since a date: Quick Add floats frequent ones up. */
    async usageSince(spaceId: string, since: IsoDate): Promise<Map<string, number>> {
      const rows = await ctx.db.all<{ category_id: string; subcategory_id: string | null; n: number }>(
        `SELECT category_id, subcategory_id, COUNT(*) AS n FROM transactions
          WHERE space_id = ? AND date >= ? AND deleted_at IS NULL
          GROUP BY category_id, subcategory_id`,
        [spaceId, since],
      );
      return new Map(rows.map((r) => [`${r.category_id}/${r.subcategory_id ?? ''}`, Number(r.n)]));
    },

    /** Date of the budget's first live entry: where its history starts. */
    async firstDate(spaceId: string): Promise<IsoDate | null> {
      const rows = await ctx.db.all<{ first: string | null }>('SELECT MIN(date) AS first FROM transactions WHERE space_id = ? AND deleted_at IS NULL', [
        spaceId,
      ]);
      return rows[0]?.first ?? null;
    },

    /** One calendar month, "YYYY-MM". */
    /**
     * Entries matching the structured filters of the entries screen, newest
     * first. Text is matched afterwards, in the app language (domain/search).
     */
    async find(filter: EntryFind): Promise<Transaction[]> {
      if (filter.spaceIds.length === 0) return [];
      const where = [`space_id IN (${filter.spaceIds.map(() => '?').join(', ')})`, 'deleted_at IS NULL'];
      const params: (string | number)[] = [...filter.spaceIds];
      const add = (sql: string, ...values: (string | number)[]) => {
        where.push(sql);
        params.push(...values);
      };
      if (filter.from) add('date >= ?', filter.from);
      if (filter.to) add('date <= ?', filter.to);
      if (filter.categoryId) add('category_id = ?', filter.categoryId);
      if (filter.subcategoryId) add('subcategory_id = ?', filter.subcategoryId);
      if (filter.paidBy) add('paid_by = ?', filter.paidBy);
      if (filter.owner === 'shared') add("ownership = 'shared'");
      else if (filter.owner) add("ownership = 'personal' AND owner_member_id = ?", filter.owner);
      if (filter.minMinor !== undefined) add('amount_minor >= ?', filter.minMinor);
      if (filter.maxMinor !== undefined) add('amount_minor <= ?', filter.maxMinor);
      const rows = await t.transactions.select(`SELECT * FROM transactions WHERE ${where.join(' AND ')} ORDER BY date DESC, created_at DESC, id DESC`, params);
      return rows;
    },

    /**
     * A month's amounts summed per category, type and "counts as spending",
     * plus how many entries it has: everything Home's totals need, without
     * sending every entry across (Phase 26: 400+ full rows took 1.2 s on a
     * phone's first read; the sums are a few dozen numbers).
     */
    async monthTotals(spaceId: string, month: string): Promise<{ items: SpendingItem[]; count: number }> {
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) throw new ValidationError('month', 'must be YYYY-MM');
      const rows = await ctx.db.all<{ category_id: string; type: TransactionType; counts: number; amount: number; n: number }>(
        `SELECT category_id, type, counts_as_spending AS counts, SUM(amount_minor) AS amount, COUNT(*) AS n FROM transactions
          WHERE space_id = ? AND date BETWEEN ? AND ? AND deleted_at IS NULL
          GROUP BY category_id, type, counts_as_spending`,
        [spaceId, `${month}-01`, `${month}-31`],
      );
      return {
        items: rows.map((r) => ({ categoryId: r.category_id, type: r.type, countsAsSpending: Boolean(r.counts), amountMinor: Number(r.amount) })),
        count: rows.reduce((n, r) => n + Number(r.n), 0),
      };
    },

    /** The newest entries of a month (Home's Recent list). */
    recentInMonth(spaceId: string, month: string, limit = 20): Promise<Transaction[]> {
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) throw new ValidationError('month', 'must be YYYY-MM');
      return t.transactions.select(
        `SELECT * FROM transactions WHERE space_id = ? AND date BETWEEN ? AND ? AND deleted_at IS NULL ORDER BY date DESC, id DESC LIMIT ?`,
        [spaceId, `${month}-01`, `${month}-31`, limit],
      );
    },

    listMonth(spaceId: string, month: string, filter?: { categoryId?: string }) {
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) throw new ValidationError('month', 'must be YYYY-MM');
      return transactions.listRange(spaceId, `${month}-01`, `${month}-31`, filter);
    },
  };

  /* ---------------------------------------------------------- recurring */

  const recurringRepo = new Repo(t.recurring, async (r: RecurringTemplate) => {
    requireText('name', r.name, 60);
    requireCurrency('currency', r.currency);
    if (r.kind === 'fixed') {
      if (r.amountMinor === null) throw new ValidationError('amountMinor', 'required for a fixed recurring expense');
      requirePositiveMinor('amountMinor', r.amountMinor);
    } else if (r.amountMinor !== null) requirePositiveMinor('amountMinor', r.amountMinor);
    if (r.dayOfMonth !== null && (!Number.isInteger(r.dayOfMonth) || r.dayOfMonth < 1 || r.dayOfMonth > 31)) {
      throw new ValidationError('dayOfMonth', 'must be 1-31');
    }
    requireIsoDate('nextDueDate', r.nextDueDate, true);
    checkOwnership(r);
    checkSpendingRule(r.type, r.countsAsSpending);
    await categoryOf(r.spaceId, r.categoryId);
    await subcategoryOf(r.categoryId, r.subcategoryId);
    // A bill outlives the people on it: its payer or owner may have left the budget since.
    if (r.paidBy) await memberOf(r.spaceId, r.paidBy, 'paidBy', true);
    if (r.ownerMemberId) await memberOf(r.spaceId, r.ownerMemberId, 'ownerMemberId', true);
  });

  async function linkedEntries(spaceId: string, from: IsoDate, to: IsoDate): Promise<Transaction[]> {
    const rows = await t.transactions.select(
      `SELECT * FROM transactions
        WHERE space_id = ? AND recurring_template_id IS NOT NULL AND date BETWEEN ? AND ? AND deleted_at IS NULL
        ORDER BY date DESC, id DESC`,
      [spaceId, from, to],
    );
    return rows;
  }

  const recurring = Object.assign(recurringRepo, {
    async createFor(input: NewRecurringInput): Promise<RecurringTemplate> {
      const space = await t.spaces.get(input.spaceId);
      if (!space) throw new NotFoundError('budget_spaces', input.spaceId);
      const type = input.type ?? 'expense';
      const rule = spendingRule(type);
      if (rule === 'choose' && input.countsAsSpending === undefined) {
        throw new ValidationError('countsAsSpending', 'say whether this credit card bill counts as spending');
      }
      await requireActivePick(input.categoryId, input.subcategoryId ?? null);
      const ownership = input.ownership ?? (space.type === 'personal' ? 'personal' : 'shared');
      const paidBy = input.paidBy ?? null;
      let ownerMemberId: string | null = null;
      if (ownership === 'personal') {
        ownerMemberId = input.ownerMemberId ?? paidBy ?? (await t.members.list({ spaceId: space.id, role: 'owner' }))[0]?.id ?? null;
      }
      const tpl = await recurringRepo.create({
        spaceId: space.id,
        categoryId: input.categoryId,
        subcategoryId: input.subcategoryId ?? null,
        name: input.name.trim(),
        kind: input.kind,
        type,
        amountMinor: input.amountMinor ?? null,
        currency: space.currency,
        frequency: input.frequency ?? 'monthly',
        dayOfMonth: input.dayOfMonth ?? null,
        nextDueDate: null,
        paidBy,
        ownership,
        ownerMemberId,
        countsAsSpending: rule === 'choose' ? input.countsAsSpending! : rule,
        active: true,
      });
      return tpl;
    },

    /** Every bill of a budget (paused ones too), oldest first. */
    listForSpace: (spaceId: string) => recurringRepo.list({ spaceId }, { orderBy: 'created_at, id' }),

    /** Entries recorded against any bill between two dates (inclusive). */
    linkedEntries,

    /**
     * Record a bill for the period containing `date` (default today): one
     * entry linked to the template, carrying its type, payer, ownership and
     * card rule, the due date and the provider used last time. Recording a
     * fixed bill with a new amount moves the fixed amount (a rent rise sticks).
     */
    async record(templateId: string, input: { addedBy: string; amountMinor?: number; date?: IsoDate }): Promise<Transaction> {
      const tpl = await t.recurring.require(templateId);
      if (!tpl.active) throw new ValidationError('templateId', 'this recurring expense is paused');
      const amountMinor = input.amountMinor ?? tpl.amountMinor;
      if (amountMinor === null) throw new ValidationError('amountMinor', 'enter the amount for this period');
      const date = input.date ?? todayIso(ctx.now());
      const period = periodOf(tpl.frequency, date);
      const linked = (await linkedEntries(tpl.spaceId, '0000-01-01', '9999-12-31')).filter((e) => e.recurringTemplateId === tpl.id);
      // Once per period: a second tap, or the same bill on another screen, must not pay it twice.
      if (linked.some((e) => e.date >= period.start && e.date <= period.end)) {
        throw new ValidationError('date', `already recorded for ${period.start}`);
      }
      const previous = linked.find((e) => e.date < period.start);
      const latest = !linked.some((e) => e.date > period.end);
      // People who have left the budget: whoever records the bill pays it, and a bill that was theirs becomes the household's.
      const stillIn = async (id: string | null) => (id ? Boolean(await t.members.get(id)) : false);
      const payerIn = await stillIn(tpl.paidBy);
      const ownerIn = tpl.ownership !== 'personal' || (await stillIn(tpl.ownerMemberId));
      let carried: DebtInput | undefined;
      if (previous && DEBT_TYPES.includes(tpl.type)) {
        const last = (await t.debt.list({ transactionId: previous.id }))[0];
        if (last)
          carried = { interestRateBp: last.interestRateBp, installmentsLeft: last.installmentsLeft === null ? null : Math.max(0, last.installmentsLeft - 1) };
      }
      const tx = await transactions.create({
        spaceId: tpl.spaceId,
        categoryId: tpl.categoryId,
        subcategoryId: tpl.subcategoryId,
        amountMinor,
        addedBy: input.addedBy,
        type: tpl.type,
        countsAsSpending: spendingRule(tpl.type) === 'choose' ? tpl.countsAsSpending : undefined,
        currency: tpl.currency,
        date,
        paidBy: payerIn ? tpl.paidBy! : input.addedBy,
        ownership: ownerIn ? tpl.ownership : 'shared',
        ownerMemberId: ownerIn ? tpl.ownerMemberId : null,
        provider: previous?.provider ?? null,
        dueDate: dueDateIn(tpl, period),
        recurringTemplateId: tpl.id,
        debt: carried,
      });
      // A new amount becomes the bill's amount only when this is its latest period (back-filling an old month changes nothing now).
      if (tpl.kind === 'fixed' && amountMinor !== tpl.amountMinor && latest) await recurringRepo.update(tpl.id, { amountMinor });
      return tx;
    },

    /**
     * The active bill an ordinary Quick Add entry settles: same category and
     * subcategory, not yet recorded in the entry's period. Keeps a bill added
     * through + from still showing as due.
     */
    async openBillFor(spaceId: string, categoryId: string, subcategoryId: string | null, date: IsoDate): Promise<RecurringTemplate | undefined> {
      const candidates = (await recurringRepo.list({ spaceId, categoryId, active: true }, { orderBy: 'created_at, id' })).filter(
        (r) => r.subcategoryId === subcategoryId,
      );
      for (const r of candidates) {
        const p = periodOf(r.frequency, date);
        const done = (await linkedEntries(spaceId, p.start, p.end)).some((e) => e.recurringTemplateId === r.id);
        if (!done) return r;
      }
      return undefined;
    },
  });

  /* ---------------------------------------------------------- consumption */

  const consumption = Object.assign(
    new Repo(t.consumption, async (c: ConsumptionData) => {
      validateConsumption(c);
      const tx = await t.transactions.get(c.transactionId);
      if (!tx || tx.spaceId !== c.spaceId) throw new ValidationError('transactionId', 'is not an entry of this budget');
    }),
    {
      forTransaction: async (transactionId: string) => (await t.consumption.list({ transactionId }))[0],
    },
  );

  /* ---------------------------------------------------------- debt details */

  const debt = Object.assign(
    new Repo(t.debt, async (d: DebtDetails) => {
      validateDebt(d);
      if (!hasDebt(d)) throw new ValidationError('debt', 'needs at least one value');
      const tx = await t.transactions.get(d.transactionId);
      if (!tx || tx.spaceId !== d.spaceId) throw new ValidationError('transactionId', 'is not an entry of this budget');
      if (!DEBT_TYPES.includes(tx.type)) throw new ValidationError('debt', 'only loan and card payments carry debt details');
    }),
    {
      forTransaction: async (transactionId: string) => (await t.debt.list({ transactionId }))[0],

      /**
       * What is known about one loan: the latest value of each field across
       * its payments (a payment may give only some of them). A loan is a
       * recurring bill, or a category/subcategory narrowed by bank.
       */
      async summaryFor(
        spaceId: string,
        of: { recurringTemplateId: string } | { categoryId: string; subcategoryId: string | null; provider?: string | null },
      ): Promise<DebtSummary> {
        const params: (string | null)[] = [spaceId];
        let where: string;
        if ('recurringTemplateId' in of) {
          where = 'tx.recurring_template_id = ?';
          params.push(of.recurringTemplateId);
        } else {
          where = `tx.category_id = ? AND ${of.subcategoryId ? 'tx.subcategory_id = ?' : 'tx.subcategory_id IS NULL'}`;
          params.push(of.categoryId);
          if (of.subcategoryId) params.push(of.subcategoryId);
          if (of.provider) {
            // "Chase", "chase " and "CHASE" are the same bank.
            where += ' AND LOWER(TRIM(tx.provider)) = LOWER(TRIM(?))';
            params.push(of.provider);
          }
        }
        const rows = await ctx.db.all<{
          remaining_balance_minor: number | null;
          interest_rate_bp: number | null;
          installments_left: number | null;
          date: string;
        }>(
          `SELECT d.remaining_balance_minor, d.interest_rate_bp, d.installments_left, tx.date
             FROM debt_details d JOIN transactions tx ON tx.id = d.transaction_id
            WHERE d.space_id = ? AND ${where} AND d.deleted_at IS NULL AND tx.deleted_at IS NULL
            ORDER BY tx.date DESC, tx.id DESC LIMIT 60`,
          params,
        );
        const latest = (col: 'remaining_balance_minor' | 'interest_rate_bp' | 'installments_left') => {
          const r = rows.find((x) => x[col] !== null);
          return r ? { value: Number(r[col]), date: r.date } : null;
        };
        return {
          remainingBalanceMinor: latest('remaining_balance_minor'),
          interestRateBp: latest('interest_rate_bp'),
          installmentsLeft: latest('installments_left'),
        };
      },
    },
  );

  /* ---------------------------------------------------------- activity (read-only; written by the repos) */

  const activity = {
    async list(spaceId: string, limit = 50): Promise<ActivityEntry[]> {
      const rows = await t.activity.select('SELECT * FROM activity_entries WHERE space_id = ? AND deleted_at IS NULL ORDER BY at DESC, id DESC LIMIT ?', [
        spaceId,
        limit,
      ]);
      return rows;
    },
    async record(entry: {
      spaceId: string;
      actorMemberId: string | null;
      action: ActivityAction;
      entity: ActivityEntry['entity'];
      entityId: string;
      summary: ActivityEntry['summary'];
    }) {
      await ctx.db.batch(activityStatement(entry));
    },
    /** Conflicts the server found in this budget that nobody has settled yet, newest first. */
    async openConflicts(spaceId: string): Promise<ActivityEntry[]> {
      const rows = await t.activity.select(
        `SELECT * FROM activity_entries
          WHERE space_id = ? AND action = 'conflict' AND deleted_at IS NULL AND json_extract(summary, '$.resolvedAt') IS NULL
          ORDER BY at DESC, id DESC`,
        [spaceId],
      );
      return rows;
    },
    /** Settle a conflict (whatever was chosen): it leaves the list on every device. */
    async resolve(id: string, resolvedBy: string | null) {
      const current = await t.activity.require(id);
      const { statements } = t.activity.prepareUpdate(current, { summary: { ...current.summary, resolvedAt: ctx.now(), resolvedBy } });
      await ctx.db.batch(statements);
    },
  };

  /* ---------------------------------------------------------- outbox */

  const outbox = {
    async pending(): Promise<
      { seq: number; table: string; rowId: string; spaceId: string | null; op: string; hlc: string; fields: Record<string, unknown> }[]
    > {
      const rows = await ctx.db.all<{ seq: number; table_name: string; row_id: string; space_id: string | null; op: string; hlc: string; fields: string }>(
        'SELECT * FROM sync_outbox ORDER BY seq',
      );
      return rows.map((r) => ({ seq: r.seq, table: r.table_name, rowId: r.row_id, spaceId: r.space_id, op: r.op, hlc: r.hlc, fields: JSON.parse(r.fields) }));
    },
  };

  /** Which budgets are read-only now (sync decides; null: none). */
  const setReadOnly = (check: ((spaceId: string) => boolean) | null) => {
    ctx.readOnly = check ?? undefined;
  };

  return { users, spaces, members, categories, subcategories, transactions, recurring, consumption, debt, activity, outbox, setReadOnly };
}
